import { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate, Outlet } from 'react-router';
import { AppShell } from '@/components/AppShell';
import { RequireAuth, RequireStaff } from '@/components/guards';
import { EmptyState, ButtonLink, Spinner } from '@/components/ui';
import { AuthCallbackPage, ForgotPasswordPage, LoginPage, SignupPage } from '@/features/auth/AuthPages';
import { HomePage } from '@/features/home/HomePage';
import { AnnouncementPage, InboxPage } from '@/features/inbox/InboxPage';
import { CoursePage } from '@/features/learn/CoursePage';
import { LearnPage } from '@/features/learn/LearnPage';
import { ModulePage } from '@/features/learn/ModulePage';
import { MedDetailPage } from '@/features/meds/MedDetailPage';
import { MedsPage } from '@/features/meds/MedsPage';
import { CreateOrgPage, JoinOrgPage } from '@/features/profile/OrgOnboarding';
import { ProfilePage } from '@/features/profile/ProfilePage';
import { LibraryPage, StateLibraryPage } from '@/features/protocols/LibraryPage';
import { ProtocolDetailPage } from '@/features/protocols/ProtocolDetailPage';
import { ProtocolsPage } from '@/features/protocols/ProtocolsPage';
import { ResourcesPage } from '@/features/resources/ResourcesPage';
import { ToolRoute, ToolsPage } from '@/features/tools/ToolsPage';

// Administration is code-split: providers in the field never download it.
const AdminLayout = lazy(() => import('@/features/admin/AdminLayout').then((m) => ({ default: m.AdminLayout })));
const AdminIndexRedirect = lazy(() => import('@/features/admin/AdminLayout').then((m) => ({ default: m.AdminIndexRedirect })));
const OverviewPage = lazy(() => import('@/features/admin/OverviewPage'));
const ProtocolsAdmin = lazy(() => import('@/features/admin/ProtocolsAdmin'));
const ProtocolEditor = lazy(() => import('@/features/admin/ProtocolEditor'));
const MedicationsAdmin = lazy(() => import('@/features/admin/MedicationsAdmin'));
const MedicationEditor = lazy(() => import('@/features/admin/MedicationEditor'));
const CoursesAdmin = lazy(() => import('@/features/admin/CoursesAdmin').then((m) => ({ default: m.CoursesAdmin })));
const CourseEditor = lazy(() => import('@/features/admin/CoursesAdmin').then((m) => ({ default: m.CourseEditor })));
const AssignmentsAdmin = lazy(() => import('@/features/admin/AssignmentsAdmin'));
const CompetenciesAdmin = lazy(() => import('@/features/admin/CompetenciesAdmin'));
const AnnouncementsAdmin = lazy(() => import('@/features/admin/AnnouncementsAdmin'));
const MembersAdmin = lazy(() => import('@/features/admin/MembersAdmin'));
const ResourcesAdmin = lazy(() => import('@/features/admin/OrgAdminPages').then((m) => ({ default: m.ResourcesAdmin })));
const SettingsAdmin = lazy(() => import('@/features/admin/OrgAdminPages').then((m) => ({ default: m.SettingsAdmin })));
const AuditAdmin = lazy(() => import('@/features/admin/OrgAdminPages').then((m) => ({ default: m.AuditAdmin })));

function Authed() {
  return (
    <RequireAuth>
      <Outlet />
    </RequireAuth>
  );
}

function Staff() {
  return (
    <RequireAuth>
      <RequireStaff>
        <Suspense fallback={<Spinner />}>
          <Outlet />
        </Suspense>
      </RequireStaff>
    </RequireAuth>
  );
}

function NotFound() {
  return (
    <EmptyState title="Page not found" action={<ButtonLink to="/">Go home</ButtonLink>}>
      The page you were looking for doesn’t exist.
    </EmptyState>
  );
}

export const router = createBrowserRouter([
  { path: '/login', element: <LoginPage /> },
  { path: '/signup', element: <SignupPage /> },
  { path: '/forgot-password', element: <ForgotPasswordPage /> },
  { path: '/reset-password', element: <Navigate to="/forgot-password" replace /> },
  { path: '/auth/callback', element: <AuthCallbackPage /> },
  {
    element: <AppShell />,
    children: [
      { index: true, element: <HomePage /> },
      // Clinical tools and the reference formulary work signed out and offline.
      { path: 'tools', element: <ToolsPage /> },
      { path: 'tools/:toolId', element: <ToolRoute /> },
      { path: 'meds', element: <MedsPage /> },
      { path: 'meds/:slug', element: <MedDetailPage /> },
      { path: 'profile', element: <ProfilePage /> },
      { path: 'join/:token', element: <JoinOrgPage /> },
      {
        element: <Authed />,
        children: [
          { path: 'protocols', element: <ProtocolsPage /> },
          { path: 'protocols/:id', element: <ProtocolDetailPage /> },
          { path: 'library', element: <LibraryPage /> },
          { path: 'library/:state', element: <StateLibraryPage /> },
          { path: 'learn', element: <LearnPage /> },
          { path: 'learn/assignments', element: <LearnPage /> },
          { path: 'learn/courses/:courseId', element: <CoursePage /> },
          { path: 'learn/courses/:courseId/modules/:moduleId', element: <ModulePage /> },
          { path: 'inbox', element: <InboxPage /> },
          { path: 'inbox/:id', element: <AnnouncementPage /> },
          { path: 'resources', element: <ResourcesPage /> },
          { path: 'orgs/new', element: <CreateOrgPage /> },
        ],
      },
      {
        path: 'admin',
        element: <Staff />,
        children: [
          { index: true, element: <AdminIndexRedirect /> },
          {
            path: ':scope',
            element: <AdminLayout />,
            children: [
              { index: true, element: <OverviewPage /> },
              { path: 'protocols', element: <ProtocolsAdmin /> },
              { path: 'protocols/:protocolId', element: <ProtocolEditor /> },
              { path: 'medications', element: <MedicationsAdmin /> },
              { path: 'medications/:medId', element: <MedicationEditor /> },
              { path: 'courses', element: <CoursesAdmin /> },
              { path: 'courses/:courseId', element: <CourseEditor /> },
              { path: 'competencies', element: <CompetenciesAdmin /> },
              { path: 'assignments', element: <AssignmentsAdmin /> },
              { path: 'announcements', element: <AnnouncementsAdmin /> },
              { path: 'members', element: <MembersAdmin /> },
              { path: 'resources', element: <ResourcesAdmin /> },
              { path: 'settings', element: <SettingsAdmin /> },
              { path: 'audit', element: <AuditAdmin /> },
            ],
          },
        ],
      },
      { path: 'home', element: <Navigate to="/" replace /> },
      { path: '*', element: <NotFound /> },
    ],
  },
]);
